/**
 * App-wide locale display rules:
 * 1) Latin digits (0-9) even for Arabic locales
 * 2) Day/month/year order (never US month/day/year)
 */

const ARABIC_DIGITS = /[٠-٩۰-۹]/g
const DIGIT_MAP = {
  '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
  '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
  '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
  '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
}

/** English locale that uses day/month/year (not en-US month/day/year) */
export const DATE_LOCALE = 'en-GB'

export function toEnglishDigits(value) {
  if (value == null) return value
  return String(value).replace(ARABIC_DIGITS, (d) => DIGIT_MAP[d] ?? d)
}

function withLatinNumbering(options) {
  if (options?.numberingSystem) return options
  return { ...(options || {}), numberingSystem: 'latn' }
}

/**
 * Map US English → British English so numeric dates are DD/MM/YYYY.
 * Leave Arabic and other locales alone (ar-EG is already day-first).
 * Default (missing locale) → en-GB so browser US settings cannot flip order.
 */
function withDayFirstLocale(locales) {
  if (locales == null) return DATE_LOCALE

  const mapOne = (locale) => {
    if (typeof locale !== 'string') return locale
    if (locale === 'en' || locale === 'en-US') return DATE_LOCALE
    if (locale.startsWith('en-US')) return locale.replace(/^en-US/, DATE_LOCALE)
    return locale
  }

  return Array.isArray(locales) ? locales.map(mapOne) : mapOne(locales)
}

function patchNumberToLocaleString() {
  const original = Number.prototype.toLocaleString
  Number.prototype.toLocaleString = function patchedNumberToLocaleString(locales, options) {
    return original.call(this, locales, withLatinNumbering(options))
  }
}

function patchDateMethod(methodName, { remapLocale }) {
  const original = Date.prototype[methodName]
  if (typeof original !== 'function') return

  Date.prototype[methodName] = function patchedDateLocaleMethod(locales, options) {
    const nextLocales = remapLocale ? withDayFirstLocale(locales) : locales
    return original.call(this, nextLocales, withLatinNumbering(options))
  }
}

function patchIntlConstructor(name, { remapLocale }) {
  const Original = Intl[name]
  if (typeof Original !== 'function') return

  function Patched(locales, options) {
    const nextLocales = remapLocale ? withDayFirstLocale(locales) : locales
    return Reflect.construct(Original, [nextLocales, withLatinNumbering(options)])
  }

  Patched.prototype = Original.prototype
  Object.defineProperty(Patched, 'name', { value: name })
  Object.defineProperty(Patched, 'supportedLocalesOf', {
    configurable: true,
    writable: true,
    value: Original.supportedLocalesOf.bind(Original),
  })

  Intl[name] = Patched
}

let applied = false

export function forceLatinDigits() {
  if (applied || typeof Intl === 'undefined') return
  applied = true

  patchNumberToLocaleString()
  // Dates + date-times: day/month/year
  patchDateMethod('toLocaleDateString', { remapLocale: true })
  patchDateMethod('toLocaleString', { remapLocale: true })
  // Times only: keep requested locale (en-US 12h vs en-GB 24h)
  patchDateMethod('toLocaleTimeString', { remapLocale: false })

  patchIntlConstructor('NumberFormat', { remapLocale: false })
  patchIntlConstructor('DateTimeFormat', { remapLocale: true })
}

forceLatinDigits()
